import { create } from 'zustand';
import { apiClient } from '../lib/api';

/** A row of people.departments, as returned by GET /people/departments. */
export interface Department {
  id: string;
  name: string;
  description: string | null;
  headId: string | null;
  employeeCount: number;
}

const normalizeDepartment = (raw: any): Department => ({
  id: raw.id,
  name: raw.name,
  description: raw.description ?? null,
  headId: raw.head_id ?? raw.headId ?? null,
  employeeCount: Number(raw.employee_count ?? raw.employeeCount ?? 0),
});

interface DepartmentState {
  departments: Department[];
  isLoading: boolean;
  isLoaded: boolean;
  error: string | null;
  fetchDepartments: () => Promise<void>;
  createDepartment: (name: string, description?: string) => Promise<Department>;
}

/**
 * Departments are master data in people.departments (managed per tenant).
 * Forms must pick from this list rather than a hardcoded one, otherwise
 * people.employees.department fills with names the table has never heard of.
 */
export const useDepartmentStore = create<DepartmentState>((set, get) => ({
  departments: [],
  isLoading: false,
  isLoaded: false,
  error: null,

  fetchDepartments: async () => {
    set({ isLoading: true, error: null });
    try {
      const response = await apiClient.get('/people/departments');
      const rows = response.data?.data ?? response.data;
      set({
        departments: Array.isArray(rows) ? rows.map(normalizeDepartment) : [],
        isLoading: false,
        isLoaded: true,
      });
    } catch (error: any) {
      set({
        isLoading: false,
        isLoaded: true,
        error: error?.response?.data?.message || 'Could not load departments',
      });
    }
  },

  createDepartment: async (name, description) => {
    const response = await apiClient.post('/people/departments', { name: name.trim(), description });
    const created = normalizeDepartment(response.data?.data ?? response.data);
    set({
      departments: [...get().departments, created].sort((a, b) => a.name.localeCompare(b.name)),
    });
    return created;
  },
}));
